import type { AssignmentSourceProbeRep, CustomAssignmentSourceProbeRep, Experiment } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import type { QueryClient } from '@tanstack/react-query';

import { createQueryOptions } from '../internal/createQueryOptions';

import { postAssignmentDataSourceProbeForQuery } from './mapAssignmentSourceProbeResponse';

// Cache so the start modal and healthcheck share one result; do not refetch on window focus.
// Healthcheck only reads this cache. Start modal POSTs on a miss or stale entry.
// Probe duration is the backend hard-block timeout (default 15s, overridable via
// custom-assignment-sources-probe-threshold-overrides), not this cache TTL.
const assignmentSourceProbeStaleTimeMs = 5 * 60 * 1000;

export const postAssignmentDataSourceProbeQuery = createQueryOptions(postAssignmentDataSourceProbeForQuery, {
  staleTime: assignmentSourceProbeStaleTimeMs,
  refetchOnWindowFocus: false,
  retry: false,
});

export type AssignmentSourceProbeQueryParams = {
  projKey: string;
  dataSourceKey: string;
  assignmentSourceExperimentId: string;
  windowStart?: number | null;
  windowEnd?: number | null;
};

export function toAssignmentSourceProbeIsoDateTime(unixMillis?: number | null): string | undefined {
  if (unixMillis === undefined || unixMillis === null) {
    return undefined;
  }

  return new Date(unixMillis).toISOString();
}

export function assignmentSourceProbeQuery({
  projKey,
  dataSourceKey,
  assignmentSourceExperimentId,
  windowStart,
  windowEnd,
}: AssignmentSourceProbeQueryParams) {
  return postAssignmentDataSourceProbeQuery({
    projKey,
    dataSourceKey,
    body: {
      assignmentSourceExperimentId,
      windowStart: toAssignmentSourceProbeIsoDateTime(windowStart),
      windowEnd: toAssignmentSourceProbeIsoDateTime(windowEnd),
    },
  });
}

export function setAssignmentSourceProbeQueryData(
  params: AssignmentSourceProbeQueryParams,
  probe: AssignmentSourceProbeRep | CustomAssignmentSourceProbeRep,
  queryClient: QueryClient = getQueryClient(),
) {
  queryClient.setQueryData(assignmentSourceProbeQuery(params).queryKey, probe);
}

export function getAssignmentSourceProbeQueryParamsFromExperiment(
  projKey: string,
  experiment: Pick<Experiment, 'draftIteration' | 'currentIteration'>,
): AssignmentSourceProbeQueryParams | undefined {
  const source = experiment.draftIteration?.assignmentDataSource ?? experiment.currentIteration?.assignmentDataSource;
  if (!source?.key || !source.experimentId) {
    return undefined;
  }

  return {
    projKey,
    dataSourceKey: source.key,
    assignmentSourceExperimentId: source.experimentId,
    windowStart: source.startTime,
    windowEnd: source.endTime,
  };
}

export function setAssignmentSourceProbeQueryDataFromExperiment(
  projKey: string,
  experiment: Pick<Experiment, 'assignmentSourceProbe' | 'draftIteration' | 'currentIteration'>,
  queryClient: QueryClient = getQueryClient(),
) {
  const probe = experiment.assignmentSourceProbe;
  const params = getAssignmentSourceProbeQueryParamsFromExperiment(projKey, experiment);
  if (!probe || !params) {
    return;
  }

  setAssignmentSourceProbeQueryData(params, probe, queryClient);
}
